<template>
    <div v-if="renderedHeightStyle" class="sidebar-hero-rendered-text" :style="renderedHeightStyle">
        <GetSectionText :section-text="section.text"/>
    </div>
    <GetSectionText v-else :section-text="section.text"/>

    <div v-if="showHeroActionsRow" :class="['actions-row', heroActionsAlignClass]">
        <CtaButton v-if="showCta()" :form-submit-button="false" :section="section" :alignment="textAlign()"/>
        <SocialIcon
            v-for="link in socialIcons"
            :key="link.type"
            :link="link"
            :fill-type="socialIconsConfig.fillType"
            :corner-radius="socialIconsConfig.cornerRadius"
            :outline-weight="socialIconsConfig.outlineWeight"
        />
    </div>

    <PortfolioNavigationBar
        v-if="showInlineNavigation"
        class="navbar-inline"
        :navigation="navigation"
        inline
        hide-logo
        hide-home
    />

    <PortfolioNavigationSocialLinks
        v-if="showLegacyInlineSocialLinks"
        :class="['navbar-inline', inlineSocialAlignClass]"
    />
</template>
<script>
import GetSectionText from "@/scripts/view/portfolio/sections/_helper/GetSectionText.vue";
import CtaButton from "@/scripts/view/portfolio/sections/_helper/CtaButton.vue";
import PortfolioNavigationBar from "@/scripts/view/portfolio/sections/common/navigation/PortfolioNavigationBar.vue";
import PortfolioNavigationSocialLinks from "@/scripts/view/portfolio/sections/common/navigation/PortfolioNavigationSocialLinks.vue";
import {
    NavigationPlacementEnum,
    SectionTypesEnum,
    SocialLinksPlacementEnum,
} from "@/scripts/common/enums.js";
import { mapGetters } from "vuex";
import SocialIcon from "@/scripts/sections/common/SocialIcon.vue";

export default {
    name: "Text",
    props: {
        section: {
            type: Object,
            required: true,
        },
        maxRenderedTextHeight: {
            type: Number,
            default: null,
        },
    },
    components: {
        GetSectionText,
        CtaButton,
        PortfolioNavigationBar,
        PortfolioNavigationSocialLinks,
        SocialIcon,
    },
    methods: {
        showCta() {
            return this.section.cta?.show && this.section.type !== SectionTypesEnum.CONTACT_FORM
        },
        textAlign() {
            return this.section.text.style && this.section.text.style.textAlign ? 'push-to-' + this.section.text.style.textAlign : ""
        },
    },
    computed: {
        ...mapGetters("view", ["viewData"]),
        portfolio() {
            return this.viewData?.portfolio;
        },
        navigation() {
            return this.portfolio?.portfolioNavigation || null;
        },
        showInlineNavigation() {
            if (this.section.type !== SectionTypesEnum.HERO) return false;
            return this.navigation?.layout?.position === NavigationPlacementEnum.UNDER_TEXT;
        },
        showLegacyInlineSocialLinks() {
            if (this.showHeroSocialIcons) return false;
            if (this.section.type !== SectionTypesEnum.HERO) return false;
            if (!this.navigation) return false;
            const position = this.navigation?.socialLinks?.position;
            if (position !== SocialLinksPlacementEnum.INLINE_BELOW_TEXT) return false;
            const links = this.portfolio?.socialLinks || [];
            return Array.isArray(links) && links.length > 0;
        },
        socialIconsConfig() {
            return this.portfolio?.templateSettings?.socialIconStyle;
        },
        socialIcons() {
            return this.section.socialIcons?.filter((link) => !!link?.url && !!link?.type);
        },
        showHeroSocialIcons() {
            return (
                this.section.type === SectionTypesEnum.HERO ||
                this.section.type === SectionTypesEnum.FOOTER
            ) && this.socialIcons.length > 0;
        },
        showHeroActionsRow() {
            return this.showHeroSocialIcons || !!this.section?.cta?.show;
        },
        heroActionsAlignClass() {
            const align = this.section?.text?.style?.textAlign || "left";
            return `align-${align}`;
        },
        renderedHeightStyle() {
            if (this.maxRenderedTextHeight === null || this.maxRenderedTextHeight === undefined) return null;

            return {
                maxHeight: `${this.maxRenderedTextHeight}px`,
                overflow: "hidden",
            };
        },
        inlineSocialAlignClass() {
            const align = this.section?.styleSettings?.boxAlign?.split('-')?.[0] || 'left';
            return `align-${align}`;
        },
    }
}
</script>

<style scoped lang="scss">
.actions-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding-top: 24px;
    position: relative;

    &.align-left {
        justify-content: flex-start;
    }

    &.align-center {
        justify-content: center;
    }

    &.align-right {
        justify-content: flex-end;
    }
}
</style>
